import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 15;
const CONTENT_WIDTH_MM = A4_WIDTH_MM - 2 * MARGIN_MM;
const CONTENT_HEIGHT_MM = A4_HEIGHT_MM - 2 * MARGIN_MM;
const SCALE = 2;

function calculatePageBreaks(
  sectionPositions: number[],
  canvasHeight: number,
  pageCanvasHeight: number,
): number[] {
  if (canvasHeight <= pageCanvasHeight) {
    return [0];
  }

  const breaks: number[] = [0];
  let currentOffset = 0;

  while (currentOffset < canvasHeight - 1) {
    const maxEnd = currentOffset + pageCanvasHeight;
    if (maxEnd >= canvasHeight) break;

    // Find the last section boundary that fits within this page
    let bestBreak = -1;
    for (const pos of sectionPositions) {
      if (pos > currentOffset && pos <= maxEnd) {
        bestBreak = pos;
      }
    }

    if (bestBreak > currentOffset) {
      breaks.push(bestBreak);
      currentOffset = bestBreak;
    } else {
      // No section boundary fits — fall back to fixed-height slicing
      breaks.push(maxEnd);
      currentOffset = maxEnd;
    }
  }

  return breaks;
}

export async function generateAppraisalPdf(
  element: HTMLElement,
  filename: string,
): Promise<void> {
  // Collect section boundary positions before rendering to canvas
  const sections = element.querySelectorAll('[data-pdf-section]');
  const sectionOffsets: number[] = [];
  for (const section of sections) {
    const sectionEl = section as HTMLElement;
    sectionOffsets.push(sectionEl.offsetTop * SCALE);
  }

  const canvas = await html2canvas(element, {
    scale: SCALE,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  });

  const pdf = new jsPDF('p', 'mm', 'a4');
  const imgWidth = CONTENT_WIDTH_MM;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  if (imgHeight <= CONTENT_HEIGHT_MM) {
    pdf.addImage(
      canvas.toDataURL('image/png'),
      'PNG',
      MARGIN_MM,
      MARGIN_MM,
      imgWidth,
      imgHeight,
    );
  } else {
    const pageCanvasHeight = (CONTENT_HEIGHT_MM / imgWidth) * canvas.width;
    const breaks = calculatePageBreaks(sectionOffsets, canvas.height, pageCanvasHeight);

    for (let i = 0; i < breaks.length; i++) {
      const yOffset = breaks[i];
      const nextBreak = i + 1 < breaks.length ? breaks[i + 1] : canvas.height;
      const sliceHeight = nextBreak - yOffset;

      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeight;
      const ctx = pageCanvas.getContext('2d');
      if (!ctx) break;

      ctx.drawImage(
        canvas,
        0,
        yOffset,
        canvas.width,
        sliceHeight,
        0,
        0,
        canvas.width,
        sliceHeight,
      );

      const pageImgHeight = (sliceHeight * imgWidth) / canvas.width;

      if (i > 0) {
        pdf.addPage();
      }

      pdf.addImage(
        pageCanvas.toDataURL('image/png'),
        'PNG',
        MARGIN_MM,
        MARGIN_MM,
        imgWidth,
        pageImgHeight,
      );
    }
  }

  pdf.save(filename);
}
